import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';

interface LessonInfo {
  id: string;
  title: string;
  date: string | null;
  start_time: string | null;
  status: string | null;
  group_id: string | null;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
}

interface RecordRow {
  id: string;
  title: string;
  lesson_id: string | null;
  group_id: string | null;
  description: string | null;
  video_url: string | null;
  alt_url: string | null;
  published: boolean;
  visible_to_students: boolean;
  date: string | null;
  duration: string | null;
}

interface RecordModalProps {
  open: boolean;
  onClose: () => void;
  record: RecordRow | null;
  lessons: LessonInfo[];
  groups: GroupInfo[];
}

export default function RecordModal({ open, onClose, record, lessons, groups }: RecordModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lessonId, setLessonId] = useState('');
  const [manualGroupId, setManualGroupId] = useState('');

  useEffect(() => {
    if (open) {
      setLessonId(record?.lesson_id ?? '');
      setManualGroupId(record?.group_id ?? '');
    } else {
      setError(null);
      setSaving(false);
    }
  }, [open, record]);

  const selectedLesson = lessons.find((l) => l.id === lessonId);
  const autoGroupId = selectedLesson?.group_id ?? null;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const title = (fd.get('title') as string || '').trim();
    const video_url = (fd.get('video_url') as string || '').trim() || null;
    const alt_url = (fd.get('alt_url') as string || '').trim() || null;
    const description = (fd.get('description') as string || '').trim() || null;
    const date = (fd.get('date') as string || '').trim() || null;
    const duration = (fd.get('duration') as string || '').trim() || null;
    const published = fd.get('published') === 'on';
    const visible_to_students = fd.get('visible_to_students') === 'on';

    // Grup: ders seçiliyse dersin grubu, değilse manuel seçim
    const group_id = lessonId ? autoGroupId : manualGroupId || null;
    const lesson_id = lessonId || null;

    if (!title) {
      setError('Lütfen kayıt başlığı girin.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = {
        title,
        lesson_id,
        group_id,
        video_url,
        alt_url,
        description,
        date,
        duration,
        published,
        visible_to_students,
      };

      if (record) {
        const { error: updateError } = await supabase
          .from('zehrahoca_lesson_records')
          .update(payload)
          .eq('id', record.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_lesson_records')
          .insert(payload);
        if (insertError) throw insertError;
      }

      form.reset();
      window.dispatchEvent(new Event('zehrahoca:records-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Kayıt kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  const autoGroupName = groups.find((g) => g.id === autoGroupId)?.name;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={record ? 'Kayıt Düzenle' : 'Yeni Kayıt Ekle'}
      subtitle={record ? 'Ders kaydı bilgilerini güncelleyin.' : 'Yeni bir ders kaydı oluşturun.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlık</label>
            <input
              type="text"
              name="title"
              defaultValue={record?.title ?? ''}
              required
              placeholder="Örn: 15 Ağustos Matematik Dersi Kaydı"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Bağlı Ders</label>
            <select
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Ders seçin</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}{l.date ? ` (${l.date})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Grup</label>
            {lessonId ? (
              <input
                type="text"
                value={autoGroupName ?? 'Dersin grubu yok'}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
              />
            ) : (
              <select
                value={manualGroupId}
                onChange={(e) => setManualGroupId(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              >
                <option value="">Grup seçin</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Video URL</label>
            <input
              type="url"
              name="video_url"
              defaultValue={record?.video_url ?? ''}
              placeholder="https://youtube.com/watch?v=... veya Google Drive linki"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Alternatif URL (yedek)</label>
            <input
              type="url"
              name="alt_url"
              defaultValue={record?.alt_url ?? ''}
              placeholder="Yedek video linki (opsiyonel)"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Açıklama</label>
            <textarea
              name="description"
              defaultValue={record?.description ?? ''}
              rows={3}
              maxLength={500}
              placeholder="Kayıt hakkında kısa açıklama..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <p className="mt-1 text-xs text-foreground-400">Maksimum 500 karakter</p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tarih</label>
            <input
              type="date"
              name="date"
              defaultValue={record?.date ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Süre</label>
            <input
              type="text"
              name="duration"
              defaultValue={record?.duration ?? ''}
              placeholder="Örn: 45 dk"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 rounded-lg bg-background-50 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              name="published"
              defaultChecked={record?.published ?? true}
              className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            Yayınla (öğrenci panelinde görünsün)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              name="visible_to_students"
              defaultChecked={record?.visible_to_students ?? true}
              className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            Öğrenciye görünür
          </label>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : record ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}